/* eslint-disable import/no-cycle */
import { PublicApplicationData } from '@flatsy/types/Applications'
import { FetchStatus } from '@flatsy/types/FetchStatus'
import { createAsyncThunk } from '@reduxjs/toolkit'
import * as URLS from 'src/constants/Api'
import { RootState } from 'src/redux'
import client, { FlatsyApiError } from 'src/utils/client'
import { getAgentId } from '@flatsy/utils/Functions/AgentId'
import { TAKE_BOOKING } from 'src/constants/Api/Application'

export const saveApplication = createAsyncThunk<
  PublicApplicationData,
  PublicApplicationData,
  {
    rejectValue: FlatsyApiError
  }
>(
  'application/saveApplication',
  async (body, { rejectWithValue }) => {
    return client<PublicApplicationData>(URLS.NEW_PUBLIC_APPLICATION(body.id), {
      body: {
        ...body,
        agent_id: getAgentId(body.agent_id),
        booking_wanted_time: body.booking_wanted_time === '' ? undefined : body.booking_wanted_time,
        booking_wanted_date: body.booking_wanted_date === '' ? undefined : body.booking_wanted_date,
      },
      method: body.id ? 'PATCH' : 'POST',
    }).catch((err) => {
      return rejectWithValue(err)
    })
  },
  {
    condition: (_, { getState }) => {
      const { saveStatus } = (getState as () => RootState)().application
      if (saveStatus === FetchStatus.LOADING) {
        return false
      }
    },
  }
)

export const completeApplication = createAsyncThunk<
  PublicApplicationData,
  string,
  {
    rejectValue: FlatsyApiError
  }
>('application/completeApplication', async (applicationId, { rejectWithValue }) => {
  return client<PublicApplicationData>(URLS.COMPLETE_PUBLIC_APPLICATION(applicationId), {
    body: {},
    method: 'POST',
  }).catch((err) => {
    return rejectWithValue(err)
  })
})

export const takeBooking = createAsyncThunk<
  {
    start_date: string
    start_time: string
    applicationId: string
  },
  {
    start_date: string
    start_time: string
    applicationId: string
  },
  {
    rejectValue: FlatsyApiError
  }
>('application/takeBooking', async ({ applicationId, ...body }, { rejectWithValue }) => {
  return client<{
    start_date: string
    start_time: string
    applicationId: string
  }>(TAKE_BOOKING(applicationId), {
    body,
    method: 'POST',
  }).catch((err) => {
    return rejectWithValue(err)
  })
})

export const getApplication = createAsyncThunk<
  PublicApplicationData,
  string,
  {
    rejectValue: FlatsyApiError
  }
>(
  'application/getApplication',
  async (applicationId, { rejectWithValue }) => {
    return client<PublicApplicationData>(URLS.NEW_PUBLIC_APPLICATION(applicationId)).catch(
      (err) => {
        return rejectWithValue(err)
      }
    )
  },
  {
    condition: (_, { getState }) => {
      const status: FetchStatus = (getState as () => RootState)().application.getStatus
      if (FetchStatus.LOADING === status) {
        // Already fetched or in progress, don't need to re-fetch
        return false
      }
    },
  }
)

export const deleteApplication = createAsyncThunk<
  PublicApplicationData,
  {
    applicationId: string
    abandon_explaination: string
    abandon_reason: string
    authentication_code: string
  },
  { rejectValue: unknown }
>('application/deleteApplication', async ({ applicationId, ...body }, { rejectWithValue }) => {
  try {
    return await client<
      PublicApplicationData,
      { abandon_explaination: string; abandon_reason: string; authentication_code: string }
    >(URLS.DELETE_PUBLIC_APPLICATION(applicationId), {
      method: 'POST',
      body,
    })
  } catch (err) {
    return rejectWithValue(err)
  }
})

export const requestCode = createAsyncThunk<
  PublicApplicationData,
  { applicationId: string; email: string },
  { rejectValue: unknown }
>('application/requestCode', async ({ applicationId, ...body }, { rejectWithValue }) => {
  try {
    return await client<PublicApplicationData, { email: string }>(
      URLS.REQUEST_CODE_PUBLIC_APPLICATION(applicationId),
      {
        method: 'POST',
        body,
      }
    )
  } catch (err) {
    return rejectWithValue(err)
  }
})

export const updateApplicationInterest = createAsyncThunk<
  PublicApplicationData,
  {
    applicationId: string
    interest: boolean
  },
  {
    rejectValue: unknown
  }
>(
  'application/updateApplicationInterest',
  async ({ applicationId, interest }, { rejectWithValue }) => {
    try {
      return await client<PublicApplicationData, { interest: boolean }>(
        URLS.PUBLIC_APPLICATION_INTEREST(applicationId),
        {
          body: { interest },
          method: 'POST',
        }
      )
    } catch (err) {
      return rejectWithValue(err)
    }
  }
)
